import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Download, CheckCircle, XCircle, Save, Plus, Upload, User, Shield, BrainCircuit, Calendar, Search, ArrowLeft, Settings, Edit, Trash2, File as FileIcon, Paperclip } from 'lucide-react';
import * as api from './api.js';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from './authConfig';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import loginIcon from './LoginIcon.svg';

// --- Root App Component ---
export default function App() {
    const { instance } = useMsal();
    const isAuthenticatedMsal = useIsAuthenticated();

    // The user object is the single source of truth for authentication state.
    const [currentUser, setCurrentUser] = useState(null);
    const isAuthenticated = !!currentUser;

    // A state to manage the initial authentication check.
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const handleLogout = () => {
        const msalAccount = instance.getActiveAccount();
        if (msalAccount) {
            // This will trigger a redirect.
            instance.logoutRedirect().catch(e => console.error(e));
        } else {
            // For local logout, just clear the user state.
            api.localLogout();
            setCurrentUser(null);
        }
    };

    useEffect(() => {
        const checkAuthAndFetchUser = async () => {
            setIsCheckingAuth(true);
            const msalAccount = instance.getActiveAccount();
            const localToken = sessionStorage.getItem('localUserToken');
            const authenticatedByProvider = !!msalAccount || !!localToken;

            if (authenticatedByProvider) {
                try {
                    // We are authenticated by a provider, now we must fetch our backend profile.
                    const userProfile = await api.getCurrentUserProfile();
                    setCurrentUser(userProfile);
                } catch (error) {
                    console.error("Failed to fetch user profile, which is required for app access. Logging out.", error);
                    // This is a critical failure. If we can't get the user profile,
                    // we log them out to prevent an inconsistent state.
                    handleLogout();
                }
            } else {
                setCurrentUser(null);
            }
            setIsCheckingAuth(false);
        };

        checkAuthAndFetchUser();
    // This effect should only run when the MSAL authentication state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticatedMsal, instance]);

    const handleLoginSuccess = async () => {
        // After a successful local login, we fetch the user profile.
        setIsCheckingAuth(true);
        try {
            const userProfile = await api.getCurrentUserProfile();
            setCurrentUser(userProfile);
        } catch (error) {
            console.error("Local login succeeded, but failed to fetch user profile.", error);
            handleLogout(); // Log out if profile fetch fails to maintain consistency.
        } finally {
            setIsCheckingAuth(false);
        }
    };
    
    // Show a loading screen during the initial auth check.
    if (isCheckingAuth) {
        return <LoadingScreen message="Authenticating..." />;
    }

    return isAuthenticated ? <MainContent onLogout={handleLogout} currentUser={currentUser} /> : <LoginPage onLoginSuccess={handleLoginSuccess} />;
}


// --- Main App Content & Routing ---
function MainContent({ onLogout, currentUser }) {
    const [view, setView] = useState(null);
    const [masterData, setMasterData] = useState(null);
    const [isAppLoading, setIsAppLoading] = useState(true);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const setupServiceWorkerListener = () => {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    console.log('Client App: Service worker is ready to communicate.');
                    
                    navigator.serviceWorker.addEventListener('message', async (event) => {
                        if (event.data && event.data.type === 'GET_TOKEN') {
                            console.log('Client App: Received GET_TOKEN request from Service Worker.');
                            
                            try {
                                const token = await api.getValidToken(); 
                                
                                if (event.ports[0]) {
                                    console.log('Client App: Sending token back to Service Worker.');
                                    event.ports[0].postMessage({ token: token });
                                }
                            } catch (error) {
                                console.error("Client App: Could not get a valid token to send to the service worker.", error);
                                if (event.ports[0]) {
                                    event.ports[0].postMessage({ token: null });
                                }
                            }
                        }
                    });
                });
            }
        };

        setupServiceWorkerListener();
    }, []);


    const fetchMasterData = useCallback(async () => {
        if (currentUser && currentUser.permissionId > 1) { 
            try {
                // Set loading state to true when starting to fetch
                setIsAppLoading(true);
                const initialData = currentUser.permissionId === 3 ? await api.getAdminMasterData() : await api.getGeneralMasterData();
                setMasterData(initialData);
            } catch (error) {
                console.error("Failed to load master data:", error);
            } finally {
                // Set loading state to false after fetching is done
                setIsAppLoading(false);
            }
        }
    }, [currentUser]);


    useEffect(() => {
        const initializeView = async () => {
            if (!currentUser) return;
            setIsAppLoading(true);
            await fetchMasterData();
            setView(currentUser.permissionId === 1 ? 'employeeDetail' : 'levelList');
            if (currentUser.permissionId === 1) setSelectedEmployeeId(currentUser.id);
            setIsAppLoading(false);
        };
        initializeView();
    }, [currentUser, fetchMasterData]);

    const handleBack = () => {
        setSearchQuery('');
        if (view === 'employeeDetail') {
            setSelectedEmployeeId(null);
            // If a level was previously selected, go back to that employee list.
            // Otherwise (e.g., for a base-level user), go back to the main level list.
            if (selectedLevel) {
                setView('employeeList');
            } else {
                setView('levelList');
            }
        }
        else if (view === 'employeeList') {
            setView('levelList');
            setSelectedLevel(null);
        }
        else if (view === 'admin') {
            setView('levelList');
        }
    };
    
    const handleDataRefresh = () => fetchMasterData();
    
    const handleSaveAdminSettings = async (newRequiredCerts) => {
        const { possibleCerts } = masterData;
        const originalCerts = await api.getRequiredCerts();
        const originalSet = new Set(originalCerts.map(c => `${c.certificationId}-${c.titleYearId}`));
        const newSet = new Set();
        
        for (const certName in newRequiredCerts) {
            const certId = possibleCerts.find(c => c.name === certName)?.id;
            if (certId) {
                newRequiredCerts[certName].forEach(titleYearId => newSet.add(`${certId}-${titleYearId}`));
            }
        }
        
        const toDelete = originalCerts.filter(c => !newSet.has(`${c.certificationId}-${c.titleYearId}`));
        const toAdd = [...newSet].filter(item => !originalSet.has(item));

        await Promise.all([
            ...toDelete.map(cert => api.deleteRequiredCert(cert.id)),
            ...toAdd.map(item => {
                const [certificationId, titleYearId] = item.split('-');
                return api.createRequiredCert({ certificationId, titleYearId: parseInt(titleYearId) });
            })
        ]);
    };

    const renderContent = () => {
        if (isAppLoading || !view || !currentUser) return <LoadingScreen />;
        if (currentUser.permissionId > 1 && !masterData) return <LoadingScreen message="Loading Master Data..." />;

        switch (view) {
            case 'levelList': 
                return <LevelListView 
                    onSelectLevel={(level) => { setSelectedLevel(level); setView('employeeList'); }} 
                    onSelectEmployee={(emp) => { 
                        setSelectedEmployeeId(emp.id); 
                        setView('employeeDetail'); 
                    }}
                    onAdminClick={() => setView('admin')} 
                    showAdminButton={currentUser.permissionId === 3} 
                />;
            case 'employeeList': 
                return <EmployeeListView 
                    level={selectedLevel} 
                    onSelectEmployee={(emp) => { setSelectedEmployeeId(emp.id); setView('employeeDetail'); }} 
                    onBack={handleBack} 
                    searchQuery={searchQuery} 
                    setSearchQuery={setSearchQuery} 
                />;
            case 'employeeDetail': 
                return <EmployeeDetailView 
                    employeeId={selectedEmployeeId} 
                    onBack={currentUser.permissionId > 1 ? handleBack : null} 
                    allData={masterData} 
                    currentUser={currentUser} 
                />;
            case 'admin': 
                return currentUser.permissionId === 3 ? <AdminView allData={masterData} onSaveSettings={handleSaveAdminSettings} onDataRefresh={handleDataRefresh} onBack={handleBack} /> : setView('levelList');
            default: 
                return <div>Error: Invalid view</div>;
        }
    };

    return (
        <div className="relative">
            <SignOutButton onLogout={onLogout} />
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Saira:wght@400;600&display=swap');`}</style>
            <div className="bg-white min-h-screen font-['Saira',_sans-serif] text-[#323A40]">{renderContent()}</div>
        </div>
    );
}

function EmployeeDetailView({ employeeId, onBack, allData, currentUser }) {
    const [localAllData, setLocalAllData] = useState(allData);
    const [isLocalLoading, setIsLocalLoading] = useState(!allData);
    const [employeeData, setEmployeeData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTitleYearId, setSelectedTitleYearId] = useState(null);
    const [viewAs, setViewAs] = useState(currentUser.permissionId === 2 ? 'manager' : 'employee');
    const [showRatingModal, setShowRatingModal] = useState(null);
    const [showAddCertModal, setShowAddCertModal] = useState(false);
    const [showEditCertModal, setShowEditCertModal] = useState(null);
    const [selectedTitle, setSelectedTitle] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [showUploadModal, setShowUploadModal] = useState(false);

    const fetchEmployeeDetails = useCallback(async () => {
        if (!employeeId) return;
        setIsLoading(true);
        try {
            const details = await api.getEmployeeDetails(employeeId);
            setEmployeeData(details);

            if (details.profile) {
                setSelectedTitleYearId(details.profile.currentTitleYearId);
                setSelectedTitle(details.profile.title);
            }
        } catch (error) {
            console.error(`Failed to fetch details for employee ${employeeId}:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        const fetchRequiredData = async () => {
            if (!localAllData) {
                setIsLocalLoading(true);
                try {
                    const initialData = await api.getGeneralMasterData();
                    setLocalAllData(initialData);
                } catch (error) {
                    console.error("Failed to load master data for detail view:", error);
                } finally {
                    setIsLocalLoading(false);
                }
            }
        };
        fetchRequiredData();
    }, [localAllData]);

    useEffect(() => {
        fetchEmployeeDetails();
    }, [fetchEmployeeDetails]);
    
    const handleDataMutation = async (mutationPromise) => {
        try {
            await mutationPromise;
            await fetchEmployeeDetails(); 
        } catch (error) {
            console.error("A data modification operation failed:", error);
            alert(`Error: ${error.message || 'An unexpected error occurred.'}`);
        }
    };
    
    const handleTitleSelectionChange = (newTitle) => {
        if (!employeeData || !localAllData) return;
        
        const relevantYearIds = new Set(employeeData.skills.map(skill => skill.titleYearId));
        if (employeeData.profile?.currentTitleYearId) {
            relevantYearIds.add(employeeData.profile.currentTitleYearId);
        }
        const relevantYears = localAllData.titleYears.filter(ty => relevantYearIds.has(ty.id));

        const yearsInTitle = relevantYears.filter(y => y.title === newTitle);
        
        if (yearsInTitle.length > 0) {
            const latestYearInTitle = yearsInTitle.sort((a, b) => b.year - a.year)[0];
            setSelectedTitle(newTitle);
            setSelectedTitleYearId(latestYearInTitle.id);
        }
    };

    const handleAddNewCert = async (certId, expirationDate, file, doesNotExpire) => { // Add doesNotExpire here
        setShowAddCertModal(false);
        await handleDataMutation(
            api.addOrUpdateEmployeeCertification({
                id: 0,
                employeeId: employeeData.profile.id,
                certificationId: certId,
                // Add this logic: if it doesn't expire, send null for the date
                expirationDate: doesNotExpire ? null : expirationDate,
                doesNotExpire: doesNotExpire, // Add the new field
            }).then(newCertRecord => {
                if (file && newCertRecord) {
                    return api.uploadAttachment(newCertRecord.id, file).then(result => {
                        if (result?.queued) {
                            alert(result.message);
                        }
                    });
                }
            })
        );
    };

    const handleUploadAttachment = async (employeeCertId, file) => {
        if (!file) return;
        await handleDataMutation(
            api.uploadAttachment(employeeCertId, file).then(result => {
                if (result?.queued) {
                    alert(result.message);
                }
            })
        );
    };

    const handleUpdateCert = async (certDto) => {
        setShowEditCertModal(null);

        if (certDto.doesNotExpire) {
            certDto.expirationDate = null;
        }
        await handleDataMutation(api.addOrUpdateEmployeeCertification(certDto));
    };

    const handleDeleteCert = async (employeeCertId) => {
        if (window.confirm("Are you sure you want to delete this certification and all its attachments?")) {
            setShowEditCertModal(null);
            await handleDataMutation(api.deleteEmployeeCertification(employeeCertId));
        }
    };

    const handleRatingChange = async (skillId, ratingType, value) => {
        if (isLocked) return;
        setShowRatingModal(null);
        await handleDataMutation(
            api.updateEmployeeSkill({
                employeeId: employeeData.profile.id,
                skillId: skillId,
                employeeRating: ratingType === 'employeeRating' ? value : employeeData.skills.find(s => s.skillId === skillId)?.employeeRating ?? 0,
                managerRating: ratingType === 'managerRating' ? value : employeeData.skills.find(s => s.skillId === skillId)?.managerRating ?? 0,
            })
        );
    };

    const handleMeetingsSave = async (updatedMeetings) => {
        if (isLocked) return;
        const originalMeetings = employeeData.meetings[selectedTitleYearId] || {};
        const updatePromises = Object.keys(updatedMeetings).map(quarterString => {
            const originalValue = originalMeetings[quarterString] || 0;
            const newValue = updatedMeetings[quarterString];
            if (newValue !== originalValue) {
                const quarterNumber = parseInt(quarterString.replace('Q', ''), 10);
                return api.updateMeeting({ employeeId: employeeData.profile.id, titleYearId: selectedTitleYearId, quarter: quarterNumber, meetingCount: newValue });
            }
            return null;
        }).filter(Boolean);
        
        if (updatePromises.length > 0) {
            await handleDataMutation(Promise.all(updatePromises));
        }
    };

    const handleAddComment = async (content) => {
        await handleDataMutation(api.addEmployeeComment({
            targetUserId: employeeId,
            content: content,
        }));
    };

    const handleUpdateComment = async (commentId, content) => {
        await handleDataMutation(api.updateEmployeeComment(commentId, content));
    };

    const handleDeleteComment = async (commentId) => {
        if (window.confirm("Are you sure you want to permanently delete this comment?")) {
            await handleDataMutation(api.deleteEmployeeComment(commentId));
        }
    };
    
    const handleSaveHeadshot = async (imageBlob) => {
        try {
            await api.uploadHeadshot(employeeId, imageBlob);
            await fetchEmployeeDetails(); // Refresh data to show the new picture
        } catch (error) {
            console.error("Failed to upload headshot:", error);
            alert("Error uploading headshot. Please try again.");
        } finally {
            setShowUploadModal(false);
        }
    };

    const handleDeleteHeadshot = async () => {
        if (window.confirm("Are you sure you want to delete this headshot permanently?")) {
            try {
                await api.deleteHeadshot(employeeId);
                await fetchEmployeeDetails(); // Refresh data to remove the picture
            } catch (error) {
                console.error("Failed to delete headshot:", error);
                alert("Error deleting headshot. Please try again.");
            }
        }
    };

    if (isLoading || isLocalLoading || !employeeData || !localAllData || selectedTitleYearId === null) return <LoadingScreen message="Loading Employee Data..." />;
    
    const { skillsMasterList, possibleCerts, titleYears } = localAllData;
    const selectedTitleYear = titleYears.find(ty => ty.id === selectedTitleYearId);
    const isLocked = selectedTitleYearId !== employeeData?.profile.currentTitleYearId;
    const canViewComments = currentUser.permissionId > 1;
    
    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <Header 
                profile={employeeData.profile} 
                viewAs={viewAs} 
                setViewAs={setViewAs} 
                selectedTitleYear={selectedTitleYear} 
                onBack={onBack} 
                currentUser={currentUser}
                onAvatarClick={() => setShowUploadModal(true)}
                onDeleteAvatar={handleDeleteHeadshot}
            />
            
            {canViewComments && (
                <div className="mt-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button onClick={() => setActiveTab('overview')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'overview' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Overview</button>
                        <button onClick={() => setActiveTab('comments')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'comments' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Comments</button>
                    </nav>
                </div>
            )}

            <main className="space-y-6 mt-6">
                {(activeTab === 'overview' || !canViewComments) && (
                    <>
                        <PerformanceChartCard 
                            skills={employeeData.skills} 
                            allTitleYears={titleYears} 
                            employeeProfile={employeeData.profile}
                            selectedTitle={selectedTitle}
                            selectedTitleYearId={selectedTitleYearId} 
                            onTitleYearSelect={setSelectedTitleYearId}
                            onTitleSelect={handleTitleSelectionChange} 
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="md:col-span-1 flex flex-col gap-6">
                                <CertificationsCard certifications={employeeData.certifications || []} onAdd={() => setShowAddCertModal(true)} onEdit={(cert) => setShowEditCertModal(cert)} requiredCerts={employeeData.requiredCerts || []} possibleCerts={possibleCerts} />
                                <OneOnOneMeetingsCard meetings={employeeData.meetings[selectedTitleYearId] || {}} viewAs={viewAs} isLocked={isLocked} onMeetingsSave={handleMeetingsSave} />
                            </div>
                            <div className="md:col-span-2">
                                 <SkillsAssessmentCard skills={employeeData.skills.filter(s => s.titleYearId === selectedTitleYearId) || []} viewAs={viewAs} onRateClick={(skillId, ratingType) => setShowRatingModal({ skillId, ratingType })} isLocked={isLocked} currentUser={currentUser} />
                            </div>
                        </div>
                    </>
                )}
                {activeTab === 'comments' && canViewComments && (
                    <CommentsSection 
                        comments={employeeData.comments || []}
                        currentUser={currentUser}
                        onAddComment={handleAddComment}
                        onUpdateComment={handleUpdateComment}
                        onDeleteComment={handleDeleteComment}
                    />
                )}
            </main>
            {showRatingModal && <RatingModal skillRating={employeeData.skills?.find(s => s.skillId === showRatingModal.skillId)} skillMaster={skillsMasterList.find(s => s.id === showRatingModal.skillId)} ratingType={showRatingModal.ratingType} onClose={() => setShowRatingModal(null)} onSave={handleRatingChange} />}
            {showAddCertModal && <AddCertModal onClose={() => setShowAddCertModal(false)} onAdd={handleAddNewCert} existingCerts={employeeData.certifications || []} possibleCerts={possibleCerts.filter(c => c.isActive)} />}
            {showEditCertModal && <EditCertModal cert={showEditCertModal} onClose={() => setShowEditCertModal(null)} onSave={handleUpdateCert} onDelete={handleDeleteCert} onUploadAttachment={handleUploadAttachment} />}
            {showUploadModal && (
                <ImageUploadModal
                    onClose={() => setShowUploadModal(false)}
                    onSave={handleSaveHeadshot}
                />
            )}
        </div>
    );
}

function CommentsSection({ comments, currentUser, onAddComment, onUpdateComment, onDeleteComment }) {
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState(null); // Will hold { id, content }
    
    const isAdmin = currentUser.permissionId === 3;

    const handleAdd = () => {
        if (newComment.trim()) {
            onAddComment(newComment.trim());
            setNewComment('');
        }
    };

    const handleStartEdit = (comment) => {
        setEditingComment({ id: comment.id, content: comment.content });
    };

    const handleCancelEdit = () => {
        setEditingComment(null);
    };

    const handleSaveEdit = () => {
        if (editingComment && editingComment.content.trim()) {
            onUpdateComment(editingComment.id, editingComment.content.trim());
            setEditingComment(null);
        }
    };

    return (
        <Card title="Comments" icon={<Calendar className="text-[#789904]" />}>
            <div className="space-y-4">
                {/* Add Comment Form */}
                <div>
                    <textarea 
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows="3"
                        placeholder="Add a new comment..."
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal"
                    />
                    <div className="flex justify-end mt-2">
                        <button onClick={handleAdd} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400" disabled={!newComment.trim()}>
                            Add Comment
                        </button>
                    </div>
                </div>

                <hr />

                {/* Comments List */}
                <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                    {comments.length > 0 ? comments.map(comment => (
                        <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="font-semibold text-sm">{comment.authorName}</p>
                                    <p className="text-xs text-gray-500">{new Date(comment.timestamp).toLocaleString()}</p>
                                </div>
                                {isAdmin && editingComment?.id !== comment.id && (
                                    <div className="flex gap-2">
                                        <button onClick={() => handleStartEdit(comment)} className="p-1 text-gray-500 hover:text-blue-600"><Edit size={16} /></button>
                                        <button onClick={() => onDeleteComment(comment.id)} className="p-1 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                                    </div>
                                )}
                            </div>
                            {editingComment?.id === comment.id ? (
                                <div className="mt-2">
                                    <textarea 
                                        value={editingComment.content}
                                        onChange={(e) => setEditingComment(c => ({...c, content: e.target.value}))}
                                        rows="3"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal"
                                    />
                                    <div className="flex justify-end gap-2 mt-2">
                                        <button onClick={handleCancelEdit} className="py-1 px-3 bg-gray-200 text-[#323A40] font-semibold uppercase text-xs rounded-lg hover:bg-gray-300">Cancel</button>
                                        <button onClick={handleSaveEdit} className="py-1 px-3 bg-[#789904] text-white font-semibold uppercase text-xs rounded-lg hover:bg-[#323A40]">Save</button>
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{comment.content}</p>
                            )}
                        </div>
                    )) : (
                        <p className="text-center text-gray-500 text-sm py-4">No comments yet.</p>
                    )}
                </div>
            </div>
        </Card>
    );
}

function CertificationsCard({ certifications, onAdd, onEdit, requiredCerts, possibleCerts }) {
    const isCertValid = (cert) => {
        if (cert.doesNotExpire) return true; // Always valid if it doesn't expire
        if (!cert.expirationDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return new Date(cert.expirationDate) >= today;
    };

    const allRelevantCerts = useMemo(() => {
        const displayedCerts = new Map();
        certifications.forEach(cert => displayedCerts.set(cert.name, { ...cert, isMissing: false, isRequired: false }));
        if (requiredCerts && possibleCerts) {
            requiredCerts.forEach(reqCert => {
                if (reqCert.isCertificationActive && !displayedCerts.has(reqCert.certificationName)) {
                    const masterCert = possibleCerts.find(c => c.id === reqCert.certificationId);
                    if (masterCert) displayedCerts.set(reqCert.certificationName, { id: `missing-${masterCert.id}`, certificationId: masterCert.id, name: reqCert.certificationName, expirationDate: null, fileAttachments: [], isMissing: true, isRequired: true });
                } else if (displayedCerts.has(reqCert.certificationName)) {
                    displayedCerts.get(reqCert.certificationName).isRequired = true;
                }
            });
        }
        return Array.from(displayedCerts.values());
    }, [certifications, requiredCerts, possibleCerts]);

    return (
        <Card title="Training & Certs" icon={<Shield className="text-[#789904]" />} actions={(<button onClick={onAdd} className="p-1.5 bg-gray-100 text-[#323A40] rounded-full hover:bg-gray-200 transition-colors"><Plus size={18} /></button>)}>
            <ul className="space-y-2">
                {allRelevantCerts.map(cert => {
                    const isValid = isCertValid(cert);
                    return (
                        <li key={cert.id} onClick={() => !cert.isMissing && onEdit(cert)} className={`p-2 rounded-lg ${!cert.isMissing ? 'cursor-pointer hover:bg-gray-50' : ''} ${cert.isRequired && !isValid ? 'border-2 border-red-500' : ''}`}>
                            <div className="flex items-center justify-between text-sm">
                                <span className="flex items-center gap-2 font-normal normal-case">
                                    {cert.isMissing ? <XCircle size={16} className="text-red-500" /> : (isValid ? <CheckCircle size={16} className="text-[#789904]" /> : <XCircle size={16} className="text-red-500" />)}
                                    {cert.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {cert.doesNotExpire ? 'Does not expire' : (cert.expirationDate ? new Date(cert.expirationDate).toLocaleDateString() : 'N/A')}
                                </span>
                            </div>
                            {!cert.isMissing && (
                                <div className="pl-6 mt-1 space-y-1">
                                    {cert.fileAttachments?.length > 0 ? (
                                        cert.fileAttachments.map(att => (
                                            <button key={att.id} onClick={(e) => { e.stopPropagation(); api.downloadAttachment(att.id, att.fileName); }} className="flex items-center gap-2 text-xs text-blue-600 hover:underline">
                                                <Paperclip size={14} /> {att.fileName}
                                            </button>
                                        ))
                                    ) : (<p className="text-xs text-gray-400 italic">No attachments</p>)}
                                </div>
                            )}
                        </li>
                    )
                })}
                {allRelevantCerts.length === 0 && <p className="text-center text-gray-500 text-sm py-4 font-normal normal-case">No certifications listed.</p>}
            </ul>
        </Card>
    );
}

function AddCertModal({ onClose, onAdd, existingCerts, possibleCerts }) {
    const [selectedCertId, setSelectedCertId] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [file, setFile] = useState(null);
    // ADD this state for the checkbox
    const [doesNotExpire, setDoesNotExpire] = useState(false);

    // UPDATE the handleAdd function to pass the new value
    const handleAdd = () => { if (selectedCertId) onAdd(parseInt(selectedCertId), expirationDate, file, doesNotExpire); };
    const availableCerts = possibleCerts.filter(c => !existingCerts.map(ec => ec.certificationId).includes(c.id));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md text-[#323A40]">
                <h3 className="text-xl font-normal normal-case mb-4">Add New Certification</h3>
                <div className="space-y-4">
                    <select value={selectedCertId} onChange={(e) => setSelectedCertId(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal">
                        <option value="" disabled>Select a certification...</option>
                        {availableCerts.map(cert => <option key={cert.id} value={cert.id}>{cert.name}</option>)}
                    </select>
                    <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Expiration Date</label>
                    {/* ADD the disabled property here */}
                    <input 
                        type="date" 
                        value={expirationDate} 
                        onChange={(e) => setExpirationDate(e.target.value)} 
                        disabled={doesNotExpire}
                        className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100" 
                    />
                    
                    {/* ADD the checkbox element */}
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={doesNotExpire} 
                            onChange={(e) => setDoesNotExpire(e.target.checked)} 
                            className="h-4 w-4 rounded border-gray-300 text-[#323A40] focus:ring-[#789904]" 
                        />
                        <span className="text-sm font-normal">This certification does not expire</span>
                    </label>
                    
                    <input type="file" id="cert-file-upload_add" className="hidden" onChange={(e) => setFile(e.target.files[0])}/>
                    <label htmlFor="cert-file-upload_add" className="w-full flex items-center justify-center gap-2 p-2 border-2 border-dashed border-gray-300 rounded-md text-[#323A40] hover:bg-gray-50 hover:border-[#789904] transition-colors uppercase font-semibold cursor-pointer">
                        <Upload size={18} /><span>{file ? file.name : 'Upload Attachment (Optional)'}</span>
                    </label>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleAdd} disabled={!selectedCertId || availableCerts.length === 0} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Add</button>
                </div>
            </div>
        </div>
    );
}

function EditCertModal({ cert, onClose, onSave, onDelete, onUploadAttachment }) {
    const [expirationDate, setExpirationDate] = useState(cert.expirationDate?.split('T')[0] || '');
    const [newFile, setNewFile] = useState(null);
    // ADD this state, initialized from the certification being edited
    const [doesNotExpire, setDoesNotExpire] = useState(cert.doesNotExpire || false);

    // UPDATE the onSave payload to include the new value
    const handleSaveMetadata = () => onSave({ 
        id: cert.id, 
        employeeId: cert.userId, 
        certificationId: cert.certificationId, 
        expirationDate,
        doesNotExpire
    });
    const handleUploadClick = () => { if (newFile) { onUploadAttachment(cert.id, newFile); setNewFile(null); } };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg text-[#323A40]">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-normal normal-case">Edit: {cert.name}</h3>
                    <button onClick={() => onDelete(cert.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full"><Trash2 size={18} /></button>
                </div>
                <div className="space-y-2">
                    <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Expiration Date</label>
                    {/* ADD the disabled property here */}
                    <input 
                        type="date" 
                        value={expirationDate} 
                        onChange={(e) => setExpirationDate(e.target.value)} 
                        disabled={doesNotExpire}
                        className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100" 
                    />

                    {/* ADD the checkbox element */}
                    <label className="flex items-center gap-2 cursor-pointer mt-2">
                        <input 
                            type="checkbox" 
                            checked={doesNotExpire} 
                            onChange={(e) => setDoesNotExpire(e.target.checked)} 
                            className="h-4 w-4 rounded border-gray-300 text-[#323A40] focus:ring-[#789904]" 
                        />
                        <span className="text-sm font-normal">This certification does not expire</span>
                    </label>

                    <div className="flex justify-end pt-2"><button onClick={handleSaveMetadata} className="py-1.5 px-4 bg-[#323A40] text-white font-semibold uppercase text-sm rounded-lg hover:bg-[#789904]">Save Date</button></div>
                </div>
                <hr className="my-4"/>
                <div>
                    <h4 className="font-semibold mb-2">Attachments</h4>
                    <div className="space-y-1 mb-4 max-h-32 overflow-y-auto">
                        {cert.fileAttachments?.length > 0 ? cert.fileAttachments.map(att => (
                            <button key={att.id} onClick={() => api.downloadAttachment(att.id, att.fileName)} className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                                <FileIcon size={16}/> {att.fileName}
                            </button>
                        )) : <p className="text-sm italic text-gray-500">No attachments found.</p>}
                    </div>
                    <div className="flex items-center gap-2 p-2 border-t">
                        <input type="file" id="cert-file-upload_edit" className="hidden" onChange={(e) => setNewFile(e.target.files[0])}/>
                        <label htmlFor="cert-file-upload_edit" className="flex-grow flex items-center justify-center gap-2 p-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:bg-gray-50 hover:border-[#789904] transition-colors uppercase font-semibold text-xs cursor-pointer">
                            <Upload size={16} /><span>{newFile ? newFile.name : 'Select a file to upload'}</span>
                        </label>
                        <button onClick={handleUploadClick} disabled={!newFile} className="bg-blue-500 text-white px-3 py-1 rounded text-sm disabled:bg-gray-300">Upload</button>
                    </div>
                </div>
                <div className="flex justify-end mt-6">
                    <button onClick={onClose} className="py-2 px-6 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Close</button>
                </div>
            </div>
        </div>
    );
}

const LoginPage = ({ onLoginSuccess }) => {
    const { instance } = useMsal();
    const [employeeNumber, setEmployeeNumber] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleMsalLogin = () => {
        instance.loginRedirect(loginRequest).catch(e => {
            console.error(e);
        });
    };

    const handleLocalLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        if (!employeeNumber || !password) {
            setError('Employee Number and Password are required.');
            setIsLoading(false);
            return;
        }
        try {
            await api.loginWithPassword(employeeNumber, password);
            onLoginSuccess();
        } catch (err) {
            setError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 font-['Saira',_sans-serif]">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-2xl shadow-lg">
                <div>
                    <img className="mx-auto h-16 w-16 mb-4" src={loginIcon} alt="Login Icon" />
                    <h2 className="text-3xl font-bold text-center text-[#323A40]">Skill Tracker</h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Sign in to continue
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleLocalLogin}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="employee-number" className="sr-only">Employee Number</label>
                            <input id="employee-number" name="employeeNumber" type="text" required value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[#789904] focus:border-[#789904] focus:z-10 sm:text-sm" placeholder="Employee Number" />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input id="password" name="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[#789904] focus:border-[#789904] focus:z-10 sm:text-sm" placeholder="Password" />
                        </div>
                    </div>
                    {error && <p className="text-sm text-red-600 text-center">{error}</p>}
                    <div><button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#323A40] hover:bg-[#789904] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#789904] disabled:bg-gray-400">{isLoading ? 'Signing In...' : 'Sign In'}</button></div>
                </form>
                <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">Or</span></div></div>
                <div><button onClick={handleMsalLogin} className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"><img className="h-5 w-5 mr-2" src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Microsoft_logo.svg/2048px-Microsoft_logo.svg.png" alt="Microsoft Logo" />Sign in with Microsoft</button></div>
            </div>
        </div>
    );
};

const SignOutButton = ({ onLogout }) => {
    return <button onClick={onLogout} className="absolute top-4 right-4 bg-gray-200 text-gray-700 py-1 px-3 rounded-lg text-sm font-semibold uppercase hover:bg-gray-300 z-10">Sign Out</button>;
};

function LoadingScreen({ message = "Loading..."}) { 
    return ( 
        <div className="flex items-center justify-center min-h-screen bg-white">
            <div className="text-center font-['Saira',_sans-serif] text-[#323A40]">
                <svg className="animate-spin h-10 w-10 text-[#323A40] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-4 text-lg font-normal">{message}</p>
            </div>
        </div> 
    );
}

function Card({ title, icon, children, actions }) { 
    return ( 
        <div className="bg-white p-5 rounded-xl border border-gray-200 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                    {icon}
                    <h2 className="text-xl font-normal normal-case text-[#323A40]">{title}</h2>
                </div>
                {actions && <div>{actions}</div>}
            </div>
            <div className="flex-grow">{children}</div>
        </div> 
    );
}

function LevelListView({ onSelectLevel, onSelectEmployee, onAdminClick, showAdminButton }) {
    const [levels, setLevels] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [levelList, employeeList] = await Promise.all([
                    api.getLevels(),
                    api.getAllEmployees()
                ]);
                setLevels(levelList || []);
                setAllEmployees(employeeList || []);
            } catch (error) {
                console.error("Failed to fetch initial navigation data:", error);
                setLevels([]);
                setAllEmployees([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredEmployees = useMemo(() => {
        if (!searchQuery) return [];
        return allEmployees.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [allEmployees, searchQuery]);

    if (isLoading) {
        return <LoadingScreen message="Loading navigation..." />;
    }

    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <header className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-normal normal-case">Employee Directory</h1>
                {showAdminButton && (
                    <button
                        onClick={onAdminClick}
                        className="flex items-center gap-2 text-sm bg-gray-100 text-[#323A40] font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-gray-200 transition-colors">
                        <Settings size={16} />
                        ADMIN
                    </button>
                )}
            </header>

            <div className="relative mb-6">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search employees..."
                    className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal"
                />
            </div>

            {searchQuery && (
                <div className="mb-8">
                    <h2 className="text-xl font-normal normal-case mb-3 text-gray-800">Search Results</h2>
                    <ul className="space-y-3">
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.map(employee => (
                                <li key={employee.id} onClick={() => onSelectEmployee(employee)} className="bg-white p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-[#789904] hover:shadow-md transition-all">
                                    <div className="flex items-center gap-4">
                                        {employee.hasHeadshot ? (
                                            <EmployeeAvatar userId={employee.id} userName={employee.name} />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-[#323A40] text-white flex items-center justify-center flex-shrink-0">
                                                <User size={24} />
                                            </div>
                                        )}
                                        <div className="flex-grow">
                                            <span className="font-normal normal-case text-base block">{employee.name}</span>
                                            <span className="text-sm text-gray-500 block">{employee.title}</span>
                                        </div>
                                    </div>
                                </li>
                            ))
                        ) : (
                            <p className="text-center text-gray-500 py-4 font-normal">No employees found matching your search.</p>
                        )}
                    </ul>
                    <hr className="mt-8 border-gray-200" />
                </div>
            )}
            
            <div>
                 <h2 className="text-xl font-normal normal-case mb-3 text-gray-800">Browse by Experience Level</h2>
                <ul className="space-y-3">
                    {levels.length > 0 ? (
                        levels.map(level => (
                            <li key={level.id} onClick={() => onSelectLevel(level)} className="bg-white p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-[#789904] hover:shadow-md transition-all">
                                <div className="font-normal normal-case">{`${level.title} - Year ${level.year}`}</div>
                            </li>
                        ))
                    ) : (
                        <p className="text-center text-gray-500 py-4 font-normal">No experience levels found.</p>
                    )}
                </ul>
            </div>
        </div>
    );
}

function EmployeeAvatar({ userId, userName }) {
    const [avatarSrc, setAvatarSrc] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchAvatar = async () => {
            // This reuses the same API function from your profile header
            const url = await api.getHeadshotUrl(userId);
            if (isMounted) {
                setAvatarSrc(url);
            }
        };

        fetchAvatar();
        return () => { isMounted = false; };
    }, [userId]);

    return (
        <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0">
            {avatarSrc ? (
                <img 
                    src={avatarSrc} 
                    alt={`${userName}'s headshot`}
                    className="w-full h-full rounded-full object-cover" 
                />
            ) : (
                <div className="w-full h-full rounded-full bg-[#323A40] text-white flex items-center justify-center">
                    <User size={24} />
                </div>
            )}
        </div>
    );
}

function EmployeeListView({ level, onSelectEmployee, onBack, searchQuery, setSearchQuery }) {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!level || !level.id) {
            setIsLoading(false);
            return;
        }

        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                const userList = await api.getEmployeesByLevel(level.id);
                setUsers(userList || []);
            } catch (error) {
                console.error(`Failed to fetch users for ${level.title}:`, error);
                setUsers([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsers();
    }, [level]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery) return users;
        return users.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [users, searchQuery]);

    if (!level) {
        return <LoadingScreen message="No level selected..." />;
    }

    const title = `Employees for ${level.title} - Year ${level.year}`;
    
    if (isLoading) {
        return (
            <div className="container mx-auto p-4 max-w-4xl">
                <header className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                        <button onClick={onBack} className="p-2 mr-4 rounded-full hover:bg-gray-100 transition-colors">
                            <ArrowLeft size={24} className="text-[#323A40]" />
                        </button>
                        <h1 className="text-2xl font-normal normal-case">{title}</h1>
                    </div>
                </header>
                <div className="text-center p-10 font-normal">Loading employees...</div>
            </div>
        );
    }
    
    return (
        <ListView 
            title={title} 
            items={filteredUsers} 
            renderItem={(user) => (
                <div className="flex items-center gap-4">
                    {user.hasHeadshot ? (
                        <EmployeeAvatar userId={user.id} userName={user.name} />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-[#323A40] text-white flex items-center justify-center flex-shrink-0">
                            <User size={24} />
                        </div>
                    )}
                    <div className="flex-grow">
                        <span className="font-normal normal-case">{user.name}</span>
                    </div>
                </div>
            )} 
            onItemClick={onSelectEmployee} 
            onBack={onBack} 
            searchQuery={searchQuery} 
            setSearchQuery={setSearchQuery} 
            searchPlaceholder="Search employees..." 
        />
    );
}

function ListView({ title, items, renderItem, onItemClick, onBack, searchQuery, setSearchQuery, searchPlaceholder, headerActions }) {
    return ( <div className="container mx-auto p-4 max-w-4xl"> <header className="flex items-center justify-between mb-6"> <div className="flex items-center">{onBack && (<button onClick={onBack} className="p-2 mr-4 rounded-full hover:bg-gray-100 transition-colors"><ArrowLeft size={24} className="text-[#323A40]" /></button>)}<h1 className="text-2xl font-normal normal-case">{title}</h1></div> {headerActions} </header> {searchPlaceholder && <div className="relative mb-6"> <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /> <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /> </div>} <ul className="space-y-3"> {items && items.length > 0 ? items.map(item => (<li key={item.id || item} onClick={() => onItemClick(item)} className="bg-white p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-[#789904] hover:shadow-md transition-all">{renderItem(item)}</li>)) : <p className="text-center text-gray-500 py-4 font-normal">No results found.</p>} </ul> </div> );
}

function AdminView({ allData, onSaveSettings, onDataRefresh, onBack }) {
    const [activeTab, setActiveTab] = useState('certs');
    const [requiredCerts, setRequiredCerts] = useState(allData.adminSettings.requiredCertsByTitleYearId || {});
    const [showInactive, setShowInactive] = useState(false);
    
    const [showAddCertModal, setShowAddCertModal] = useState(false);
    const [showEditCertModal, setShowEditCertModal] = useState(null);
    const [showAddSkillModal, setShowAddSkillModal] = useState(false);
    const [showEditSkillModal, setShowEditSkillModal] = useState(null);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(null);

    const handleTitleYearToggle = (certName, titleYearId) => { setRequiredCerts(prev => { const newCerts = { ...prev }; const titleYearIds = newCerts[certName] || []; if (titleYearIds.includes(titleYearId)) { newCerts[certName] = titleYearIds.filter(id => id !== titleYearId); } else { newCerts[certName] = [...titleYearIds, titleYearId].sort(); } return newCerts; }); };
    const handleAddCertType = async (newName) => { await api.createMasterCertification({ name: newName, isActive: true }); setShowAddCertModal(false); await onDataRefresh(); };
    const handleEditCertType = async (certId, newName, isActive) => { await api.updateMasterCertification(certId, { id: certId, name: newName, isActive }); setShowEditCertModal(null); await onDataRefresh(); };
    const handleAddSkill = async (newSkill) => {
        const category = allData.skillCategories.find(c => c.name === newSkill.categoryName);
        if (category) {
            const payload = { ...newSkill, categoryId: category.id };
            delete payload.categoryName;
            await api.createMasterSkill(payload);
        } else {
            console.error("Could not find category ID for name:", newSkill.categoryName);
        }
        setShowAddSkillModal(false);
        await onDataRefresh();
    };
    const handleEditSkill = async (skillId, updatedSkill) => {
        const category = allData.skillCategories.find(c => c.name === updatedSkill.categoryName);

        if (!category) {
            console.error("Could not find category ID for name:", updatedSkill.categoryName);
            return;
        }
        const payload = {
            id: updatedSkill.id,
            name: updatedSkill.name,
            titleYearId: updatedSkill.titleYearId,
            categoryId: category.id,
            isActive: updatedSkill.isActive,
            descriptionLevel1: updatedSkill.descriptionLevel1,
            descriptionLevel2: updatedSkill.descriptionLevel2,
            descriptionLevel3: updatedSkill.descriptionLevel3,
            descriptionLevel4: updatedSkill.descriptionLevel4,
            descriptionLevel5: updatedSkill.descriptionLevel5
        };

        try {
            await api.updateMasterSkill(skillId, payload);
            setShowEditSkillModal(null);
            await onDataRefresh();
        } catch (error) {
            console.error("Failed to save skill:", error);
        }
    };
    const handleAddCategory = async (newName) => { await api.createSkillCategory({ name: newName, isActive: true }); setShowAddCategoryModal(false); await onDataRefresh(); };
    const handleEditCategory = async (catId, newName, isActive) => { await api.updateSkillCategory(catId, { id: catId, name: newName, isActive }); setShowEditCategoryModal(null); await onDataRefresh(); };
    
    const skillsByTitleYear = useMemo(() => {
        const grouped = {};
        const activeCategoryNames = allData.skillCategories.filter(c => c.isActive).map(c => c.name);
        const filteredSkills = allData.skillsMasterList.filter(s => showInactive || (s.isActive && activeCategoryNames.includes(s.categoryName)));
        filteredSkills.forEach(skill => {
            const titleYearId = skill.titleYearId;
            if (!grouped[titleYearId]) grouped[titleYearId] = {};
            if (!grouped[titleYearId][skill.categoryName]) grouped[titleYearId][skill.categoryName] = [];
            grouped[titleYearId][skill.categoryName].push(skill);
        });
        return grouped;
    }, [allData.skillsMasterList, allData.skillCategories, showInactive]);
    
        return (
        <div className="container mx-auto p-4 max-w-4xl space-y-6">
            <header className="flex items-center"> <button onClick={onBack} className="p-2 mr-4 rounded-full hover:bg-gray-100 transition-colors"><ArrowLeft size={24} className="text-[#323A40]" /></button> <h1 className="text-2xl font-normal normal-case">Admin Settings</h1> </header>
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button onClick={() => setActiveTab('certs')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'certs' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Certifications</button>
                    <button onClick={() => setActiveTab('skills')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'skills' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Skills</button>
                    <button onClick={() => setActiveTab('categories')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'categories' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Categories</button>
                    <button onClick={() => setActiveTab('users')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'users' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Users</button>
                    <button onClick={() => setActiveTab('titles')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold uppercase text-sm ${activeTab === 'titles' ? 'border-[#323A40] text-[#323A40]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Titles</button>
                </nav>
                    </div>
            <div className="flex justify-end items-center"><label className="flex items-center cursor-pointer"><span className="mr-3 text-sm font-semibold uppercase text-gray-600">Show Inactive</span><div className="relative"><input type="checkbox" checked={showInactive} onChange={() => setShowInactive(!showInactive)} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div></div></label></div>
            {activeTab === 'certs' && (<div className="bg-white p-5 rounded-xl border border-gray-200"><div className="flex justify-between items-center mb-4"> <h3 className="font-semibold text-[#789904] uppercase">Required Certifications by Title/Year</h3> <button onClick={() => setShowAddCertModal(true)} className="flex items-center gap-2 text-sm bg-[#323A40] text-white font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-[#789904] transition-colors"><Plus size={16} />Add New Cert Type</button> </div><div className="space-y-3">{allData.possibleCerts.filter(c => showInactive || c.isActive).map(cert => ( <div key={cert.id} className={`grid grid-cols-2 items-center gap-4 p-2 rounded-lg hover:bg-gray-50 ${!cert.isActive && 'opacity-50'}`}> <div className="flex items-center gap-2"> <button onClick={() => setShowEditCertModal(cert)} className="p-1 text-gray-400 hover:text-[#323A40]"><Edit size={16} /></button> <span className="font-normal normal-case">{cert.name} {!cert.isActive && '(Inactive)'}</span> </div> <div className="flex items-center justify-end gap-2 flex-wrap"> {allData.titleYears.filter(ty => showInactive || ty.isActive).map(titleYear => ( <label key={titleYear.id} className="flex items-center gap-1 cursor-pointer text-sm"> <input type="checkbox" checked={(requiredCerts[cert.name] || []).includes(titleYear.id)} onChange={() => handleTitleYearToggle(cert.name, titleYear.id)} className="h-4 w-4 rounded border-gray-300 text-[#323A40] focus:ring-[#789904]" /> {`${titleYear.title} - ${titleYear.year}`} </label> ))} </div> </div> ))}</div></div>)}
            {activeTab === 'skills' && (<div className="bg-white p-5 rounded-xl border border-gray-200"><div className="flex justify-between items-center mb-4"><h3 className="font-semibold text-[#789904] uppercase">Skills Management</h3><button onClick={() => setShowAddSkillModal(true)} className="flex items-center gap-2 text-sm bg-[#323A40] text-white font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-[#789904] transition-colors"><Plus size={16} />Add New Skill</button></div><div className="space-y-4">{Object.keys(skillsByTitleYear).sort().map(titleYearId => { const titleYear = allData.titleYears.find(ty => ty.id === parseInt(titleYearId)); return (<div key={titleYearId}><h4 className="font-semibold text-lg text-[#323A40] mb-2">{titleYear ? `${titleYear.title} - Year ${titleYear.year}` : `Title Year ${titleYearId}`}</h4>{Object.keys(skillsByTitleYear[titleYearId]).sort().map(category => (<div key={category} className={`pl-4 mb-3 ${!allData.skillCategories.find(c=>c.name === category)?.isActive && !showInactive ? 'hidden' : ''}`}><h5 className="font-semibold text-md text-gray-600 italic mb-2">{category}</h5><ul className="space-y-2">{skillsByTitleYear[titleYearId][category].map(skill => ( <li key={skill.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 ${!skill.isActive && 'opacity-50'}`}><button onClick={() => setShowEditSkillModal(skill)} className="p-1 text-gray-400 hover:text-[#323A40]"><Edit size={16}/></button><span className="font-normal normal-case">{skill.name} {!skill.isActive && '(Inactive)'}</span></li>))}</ul></div>))}</div>)})}</div></div>)}
            {activeTab === 'categories' && (<div className="bg-white p-5 rounded-xl border border-gray-200"><div className="flex justify-between items-center mb-4"><h3 className="font-semibold text-[#789904] uppercase">Skill Category Management</h3><button onClick={() => setShowAddCategoryModal(true)} className="flex items-center gap-2 text-sm bg-[#323A40] text-white font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-[#789904] transition-colors"><Plus size={16} />Add New Category</button></div><ul className="space-y-2">{allData.skillCategories.filter(c => showInactive || c.isActive).map(cat => ( <li key={cat.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 ${!cat.isActive && 'opacity-50'}`}><button onClick={() => setShowEditCategoryModal(cat)} className="p-1 text-gray-400 hover:text-[#323A40]"><Edit size={16}/></button><span className="font-normal normal-case">{cat.name} {!cat.isActive && '(Inactive)'}</span></li>))}</ul></div>)}
            {activeTab === 'users' && ( 
                <UserManagementView 
                    showInactive={showInactive} 
                    onDataRefresh={onDataRefresh} 
                    titleYears={allData.titleYears} 
                /> 
            )}
            {activeTab === 'titles' && ( <TitleYearManagementView showInactive={showInactive} /> )}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={async () => {
                        try {
                            await onSaveSettings(requiredCerts);
                            await onDataRefresh();
                            onBack();
                        } catch (error) {
                            console.error("Failed to save admin settings:", error);
                            alert("Error saving settings. Please check the console and try again.");
                        }
                    }}
                    className="flex items-center gap-2 bg-[#323A40] text-white uppercase font-semibold py-2 px-6 rounded-lg shadow-md hover:bg-[#789904] transition-colors"
                >
                    <Save size={18} /> Save & Exit
                </button>
            </div>
            {showAddCertModal && <AddCertTypeModal onClose={() => setShowAddCertModal(false)} onSave={handleAddCertType} />}
            {showEditCertModal && <EditCertTypeModal cert={showEditCertModal} onClose={() => setShowEditCertModal(null)} onSave={handleEditCertType} />}
            {showAddSkillModal && <AddSkillModal onClose={() => setShowAddSkillModal(false)} onSave={handleAddSkill} categories={allData.skillCategories.filter(c => c.isActive)} titleYears={allData.titleYears} />}
            {showEditSkillModal && <EditSkillModal skill={showEditSkillModal} onClose={() => setShowEditSkillModal(null)} onSave={handleEditSkill} categories={allData.skillCategories.filter(c => c.isActive)} titleYears={allData.titleYears}/>}
            {showAddCategoryModal && <AddCategoryModal onClose={() => setShowAddCategoryModal(false)} onSave={handleAddCategory} />}
            {showEditCategoryModal && <EditCategoryModal category={showEditCategoryModal} onClose={() => setShowEditCategoryModal(null)} onSave={handleEditCategory} />}
            </div>
        );
    }

    function TitleYearManagementView({ showInactive }) {
        const [titleYears, setTitleYears] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [showAddModal, setShowAddModal] = useState(false);
        const [showEditModal, setShowEditModal] = useState(null);
        const [modalError, setModalError] = useState(null);
    
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await api.getAdminTitleYears();
                setTitleYears(data || []);
            } catch (error) {
                console.error("Failed to fetch title years:", error);
            } finally {
                setIsLoading(false);
            }
        };
    
        useEffect(() => {
            fetchData();
        }, []);
    
        const handleSave = async (dto) => {
            setModalError(null);
            try {
                if (dto.id) {
                    await api.updateTitleYear(dto.id, dto);
                } else {
                    await api.createTitleYear(dto);
                }
                setShowAddModal(false);
                setShowEditModal(null);
                fetchData();
            } catch (error) {
                setModalError(error.message);
            }
        };
    
        if (isLoading) return <LoadingScreen message="Loading Titles..." />;
        
        return (
            <div className="bg-white p-5 rounded-xl border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-[#789904] uppercase">Title & Year Management</h3>
                    <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 text-sm bg-[#323A40] text-white font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-[#789904] transition-colors">
                        <Plus size={16} /> Add New
                    </button>
                    </div>
                <ul className="space-y-2">
                    {titleYears.filter(ty => showInactive || ty.isActive).map(ty => (
                        <li key={ty.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 ${!ty.isActive && 'opacity-50'}`}>
                            <button onClick={() => setShowEditModal(ty)} className="p-1 text-gray-400 hover:text-[#323A40]"><Edit size={16}/></button>
                            <span className="font-normal normal-case flex-1">{ty.title} - Year {ty.year}</span>
                            <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${ty.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'}`}>
                                {ty.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </li>
                    ))}
                </ul>
                {(showAddModal || showEditModal) && 
                    <TitleYearModal 
                        titleYear={showEditModal} 
                        onClose={() => { setShowAddModal(false); setShowEditModal(null); setModalError(null); }} 
                        onSave={handleSave} 
                        error={modalError}
                    />
                }
            </div>
        );
    }

function TitleYearModal({ titleYear, onClose, onSave, error }) {
    const [dto, setDto] = useState({
        id: titleYear?.id || 0,
        title: titleYear?.title || '',
        year: titleYear?.year || 1,
        isActive: titleYear?.isActive ?? true
    });

    const handleFieldChange = (field, value) => {
        setDto(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
                <h3 className="text-xl font-normal normal-case mb-4">{dto.id ? 'Edit' : 'Add'} Title/Year</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Title</label>
                        <input type="text" value={dto.title} onChange={(e) => handleFieldChange('title', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                </div>
                    <div>
                        <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Year</label>
                        <input type="number" value={dto.year} onChange={(e) => handleFieldChange('year', parseInt(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md" />
                </div>
                    <label className="flex items-center justify-between w-full cursor-pointer pt-4">
                        <span className="text-sm font-semibold uppercase text-gray-600">Active</span>
                        <div className="relative">
                            <input type="checkbox" checked={dto.isActive} onChange={(e) => handleFieldChange('isActive', e.target.checked)} className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div>
                        </div>
                    </label>
                </div>
                {error && <p className="text-red-500 text-sm mt-4 text-center bg-red-50 p-3 rounded-lg">{error}</p>}
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onSave(dto)} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904]">Save</button>
                </div>
            </div>
        </div>
    );
}


function UserManagementView({ showInactive, onDataRefresh, titleYears }) {
    const [users, setUsers] = useState([]);
    const [permissions] = useState([
        { id: 1, name: 'User' },
        { id: 2, name: 'Manager' },
        { id: 3, name: 'Admin' },
    ]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(null);

    const fetchUsers = async () => {
        const userList = await api.getUsers();
        setUsers(userList || []);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleCreateUser = async (userRequest) => {
        try {
            await api.createUser(userRequest);
            setShowAddModal(false);
            await fetchUsers();
        } catch (error) {
            console.error("Failed to create user:", error);
            alert(`Error: ${error.message}`);
        }
    };

    const handleUpdateUser = async (userId, userDto) => {
        try {
            if (userDto) {
                await api.updateUser(userId, userDto);
            }
            setShowEditModal(null);
            await fetchUsers();
        } catch (error) {
            console.error("Failed to update user:", error);
            if (userDto) {
                alert(`Error: ${error.message}`);
            }
        }
    };
    
    const handleAdminUploadHeadshot = async (userId, imageBlob) => {
        try {
            await api.uploadHeadshot(userId, imageBlob);
            await fetchUsers(); 
        } catch (error) {
            console.error("Admin failed to upload headshot:", error);
            alert("Error uploading headshot.");
        }
    };

    const handleAdminDeleteHeadshot = async (userId) => {
        if (window.confirm("Are you sure you want to delete this user's headshot?")) {
            try {
                await api.deleteHeadshot(userId);
                await fetchUsers();
            } catch (error) {
                console.error("Admin failed to delete headshot:", error);
                alert("Error deleting headshot.");
            }
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => showInactive || user.isActive);
    }, [users, showInactive]);

    return (
        <div className="bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-[#789904] uppercase">User Management</h3>
                <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 text-sm bg-[#323A40] text-white font-semibold uppercase py-1.5 px-3 rounded-lg hover:bg-[#789904] transition-colors">
                    <Plus size={16} /> Add New User
                </button>
            </div>
            <ul className="space-y-2">
                {filteredUsers.map(user => (
                    <li key={user.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 ${!user.isActive && 'opacity-50'}`}>
                        <button onClick={() => setShowEditModal(user)} className="p-1 text-gray-400 hover:text-[#323A40]"><Edit size={16}/></button>
                        <span className="font-normal normal-case flex-1">{user.displayName} ({user.email || 'No Email'})</span>
                        <span className="text-sm text-gray-600">{permissions.find(p => p.id === user.permissionId)?.name}</span>
                        <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${user.email ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-800'}`}>
                            {user.email ? 'SSO' : 'Local'}
                        </span>
                    </li>
                ))}
            </ul>
            {showAddModal && <AddUserModal onClose={() => setShowAddModal(false)} onSave={handleCreateUser} permissions={permissions} titleYears={titleYears} />}
            {showEditModal && <EditUserModal 
                user={showEditModal} 
                onClose={() => setShowEditModal(null)} 
                onSave={handleUpdateUser} 
                permissions={permissions} 
                titleYears={titleYears}
                onUploadHeadshot={handleAdminUploadHeadshot}
                onDeleteHeadshot={handleAdminDeleteHeadshot}
            />}
        </div>
    );
}

function AddUserModal({ onClose, onSave, permissions, titleYears }) {
    const [isSsoUser, setIsSsoUser] = useState(false);
    const [userData, setUserData] = useState({
        employeeNumber: '',
        displayName: '',
        currentTitleYearId: titleYears?.find(ty => ty.isActive)?.id || '',
        email: '',
        password: '',
        permissionId: 1
    });
    const [error, setError] = useState('');

    const handleFieldChange = (field, value) => {
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        if (!userData.employeeNumber || !userData.displayName) {
            setError('Employee Number and Display Name are required.');
            return;
        }
        if (isSsoUser && !userData.email) {
            setError('Email is required for SSO users.');
            return;
        }
        if (!isSsoUser && !userData.password) {
            setError('Password is required for local users.');
            return;
        }

        const requestPayload = {
            employeeNumber: parseInt(userData.employeeNumber, 10),
            displayName: userData.displayName,
            currentTitleYearId: userData.currentTitleYearId,
            email: isSsoUser ? userData.email : null,
            permissionId: parseInt(userData.permissionId, 10),
            password: isSsoUser ? null : userData.password
        };
        setError('');
        onSave(requestPayload);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg text-[#323A40]">
                <h3 className="text-xl font-normal normal-case mb-4">Add New User</h3>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    <div className="flex items-center justify-end">
                        <label className="flex items-center cursor-pointer">
                            <span className="mr-3 text-sm font-semibold uppercase text-gray-600">
                                {isSsoUser ? 'SSO User (Email)' : 'Local User (Password)'}
                            </span>
                            <div className="relative">
                                <input type="checkbox" checked={isSsoUser} onChange={() => setIsSsoUser(!isSsoUser)} className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div>
                            </div>
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Employee Number *</label>
                            <input type="number" value={userData.employeeNumber} onChange={(e) => handleFieldChange('employeeNumber', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                         <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Display Name *</label>
                            <input type="text" value={userData.displayName} onChange={(e) => handleFieldChange('displayName', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                     <div>
                        <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Title</label>
                        <select value={userData.currentTitleYearId} onChange={(e) => handleFieldChange('currentTitleYearId', parseInt(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md">
                            <option value="">-- Select Title --</option>
                            {titleYears && titleYears.map(ty => (
                                <option key={ty.id} value={ty.id}>{ty.title} - Year {ty.year}</option>
                            ))}
                        </select>
                    </div>
                     <div>
                        <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Permission Level *</label>
                        <select value={userData.permissionId} onChange={(e) => handleFieldChange('permissionId', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md">
                            {permissions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    {isSsoUser ? (
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Email *</label>
                            <input type="email" value={userData.email} onChange={(e) => handleFieldChange('email', e.target.value)} placeholder="user@company.com" className="w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Password *</label>
                            <input type="password" value={userData.password} onChange={(e) => handleFieldChange('password', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    )}
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904]">Save User</button>
                </div>
            </div>
        </div>
    );
}

function EditUserModal({ user, onClose, onSave, permissions, titleYears, onUploadHeadshot, onDeleteHeadshot }) {
    const [userData, setUserData] = useState({
        employeeNumber: user.employeeNumber,
        displayName: user.displayName,
        currentTitleYearId: user.currentTitleYearId || '',
        email: user.email || '',
        permissionId: user.permissionId,
        isActive: user.isActive
    });
    const [error, setError] = useState('');
    const [showConvertToLocal, setShowConvertToLocal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showConvertToSso, setShowConvertToSso] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetPasswordValue, setResetPasswordValue] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);

    const [avatarSrc, setAvatarSrc] = useState(null);
    useEffect(() => {
        const fetchAvatar = async () => {
            if (user.hasHeadshot) {
                const url = await api.getHeadshotUrl(user.id);
                setAvatarSrc(url);
            } else {
                setAvatarSrc(null);
            }
        };
        fetchAvatar();
    }, [user.hasHeadshot, user.id]);

    const handleSaveHeadshot = (imageBlob) => {
        onUploadHeadshot(user.id, imageBlob);
        setShowUploadModal(false);
    };

    const handleFieldChange = (field, value) => {
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setError(''); 
        if (showResetPassword) {
            if (!resetPasswordValue) {
                setError('A new password is required to perform the reset.');
                return;
            }
            try {
                await api.resetUserPassword(user.id, resetPasswordValue);
                onSave(user.id, null);
            } catch (err) {
                setError(err.message || "Failed to reset password.");
            }
            return;
        }

        if (showConvertToLocal) {
            if (!newPassword) {
                setError('A new password is required to convert the account.');
                return;
            }
            try {
                await api.convertUserToLocal(user.id, newPassword);
                onSave(user.id, null);
            } catch (err) {
                setError(err.message || "Failed to convert user to local.");
            }
            return;
        }

        if (showConvertToSso) {
            if (!newUserEmail) {
                setError('An email is required to convert the account to SSO.');
                return;
            }
            try {
                await api.convertUserToSso(user.id, newUserEmail);
                onSave(user.id, null);
            } catch (err) {
                setError(err.message || "Failed to convert user to SSO.");
            }
            return;
        }
        
        if (!userData.employeeNumber || !userData.displayName) {
            setError('Employee Number and Display Name are required.');
            return;
        }

        const payload = {
            id: user.id,
            employeeNumber: parseInt(userData.employeeNumber, 10),
            displayName: userData.displayName,
            currentTitleYearId: userData.currentTitleYearId ? parseInt(userData.currentTitleYearId, 10) : null,
            email: userData.email,
            permissionId: parseInt(userData.permissionId, 10),
            isActive: userData.isActive
        };

        onSave(user.id, payload);
    };

    const isSsoUser = !!user.email;
    const isActionActive = showConvertToLocal || showConvertToSso || showResetPassword;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
                <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg text-[#323A40]">
                    <h3 className="text-xl font-normal normal-case mb-4">Edit User: {user.displayName}</h3>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                        <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-gray-200 flex-shrink-0">
                                {avatarSrc ? (
                                    <img src={avatarSrc} alt="User headshot" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    <div className="bg-[#323A40] text-white w-full h-full rounded-full flex items-center justify-center">
                                        <User size={32} />
                                    </div>
                                )}
                            </div>
                            <div className="flex-grow">
                                <p className="font-semibold uppercase text-sm text-[#789904]">Profile Picture</p>
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => setShowUploadModal(true)} className="py-1 px-3 bg-blue-500 text-white font-semibold uppercase text-xs rounded-lg hover:bg-blue-600">Upload New</button>
                                    <button onClick={() => onDeleteHeadshot(user.id)} disabled={!avatarSrc} className="py-1 px-3 bg-red-500 text-white font-semibold uppercase text-xs rounded-lg hover:bg-red-600 disabled:bg-gray-300">Delete</button>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-100 p-3 rounded-lg text-sm">
                            <strong>User Type:</strong> {isSsoUser ? 'SSO (Email)' : 'Local (Password)'}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Employee Number *</label>
                                <input type="number" value={userData.employeeNumber} onChange={(e) => handleFieldChange('employeeNumber', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Display Name *</label>
                                <input type="text" value={userData.displayName} onChange={(e) => handleFieldChange('displayName', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Title</label>
                            <select value={userData.currentTitleYearId} onChange={(e) => handleFieldChange('currentTitleYearId', parseInt(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md">
                                <option value="">-- Select Title --</option>
                                {titleYears && titleYears.map(ty => (
                                    <option key={ty.id} value={ty.id}>{ty.title} - Year {ty.year}</option>
                                ))}
                            </select>
                        </div>
                        {isSsoUser && (
                            <div>
                               <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Email</label>
                               <input type="email" value={userData.email} onChange={(e) => handleFieldChange('email', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                           </div>
                        )}
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Permission Level *</label>
                            <select value={userData.permissionId} onChange={(e) => handleFieldChange('permissionId', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md">
                                {permissions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center cursor-pointer pt-4">
                            <span className="mr-3 text-sm font-semibold uppercase text-gray-600">Active</span>
                            <div className="relative">
                                <input type="checkbox" checked={userData.isActive} onChange={(e) => handleFieldChange('isActive', e.target.checked)} className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div>
                            </div>
                        </label>
                        <div className="pt-4 border-t mt-4 space-y-3">
                            {!isSsoUser && !isActionActive && (
                                 <button onClick={() => setShowResetPassword(true)} className="w-full text-center py-2 px-4 bg-orange-400 text-orange-900 font-semibold uppercase rounded-lg hover:bg-orange-500">
                                    Reset Password
                                </button>
                            )}
                            {isSsoUser && !isActionActive && (
                                <button onClick={() => setShowConvertToLocal(true)} className="w-full text-center py-2 px-4 bg-yellow-400 text-yellow-900 font-semibold uppercase rounded-lg hover:bg-yellow-500">
                                    Convert to Local Account
                                </button>
                            )}
                            {!isSsoUser && !isActionActive && (
                                 <button onClick={() => setShowConvertToSso(true)} className="w-full text-center py-2 px-4 bg-blue-500 text-white font-semibold uppercase rounded-lg hover:bg-blue-600">
                                    Convert to SSO Account
                                </button>
                            )}
                            {showResetPassword && (
                                <div>
                                    <label className="block text-sm font-semibold uppercase text-red-600 mb-1">Enter New Password *</label>
                                    <input type="password" value={resetPasswordValue} onChange={(e) => setResetPasswordValue(e.target.value)} className="w-full p-2 border border-red-300 rounded-md" placeholder="Enter a strong new password" />
                                    <button onClick={() => { setShowResetPassword(false); setError(''); }} className="text-xs text-gray-500 hover:underline mt-1">Cancel Reset</button>
                                </div>
                            )}
                            {showConvertToLocal && (
                                <div>
                                    <label className="block text-sm font-semibold uppercase text-red-600 mb-1">New Password *</label>
                                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full p-2 border border-red-300 rounded-md" placeholder="Enter a new password" />
                                    <p className="text-xs text-gray-500 mt-1">The user will no longer be able to sign in with SSO.</p>
                                    <button onClick={() => { setShowConvertToLocal(false); setError(''); }} className="text-xs text-gray-500 hover:underline mt-1">Cancel Conversion</button>
                                </div>
                            )}
                            {showConvertToSso && (
                                 <div>
                                    <label className="block text-sm font-semibold uppercase text-red-600 mb-1">User's Email *</label>
                                    <input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="w-full p-2 border border-red-300 rounded-md" placeholder="Enter the user's SSO email" />
                                    <p className="text-xs text-gray-500 mt-1">The user's password will be removed and they will only be able to sign in with SSO.</p>
                                    <button onClick={() => { setShowConvertToSso(false); setError(''); }} className="text-xs text-gray-500 hover:underline mt-1">Cancel Conversion</button>
                                </div>
                            )}
                        </div>
                        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                        <button onClick={handleSave} className={`py-2 px-6 text-white font-semibold uppercase rounded-lg transition-colors ${ isActionActive ? 'bg-red-600 hover:bg-red-700' : 'bg-[#323A40] hover:bg-[#789904]' }`}>
                            {showResetPassword ? 'Confirm Reset' : (showConvertToLocal || showConvertToSso ? 'Confirm Conversion' : 'Save Changes')}
                        </button>
                    </div>
                </div>
            </div>
            {showUploadModal && <ImageUploadModal onClose={() => setShowUploadModal(false)} onSave={handleSaveHeadshot} />}
        </>
    );
}

function Header({ profile, viewAs, setViewAs, selectedTitleYear, onBack, currentUser, onAvatarClick, onDeleteAvatar }) {
    const canEditAvatar = currentUser.id === profile.id || currentUser.permissionId > 1;

    const [avatarSrc, setAvatarSrc] = useState(null);
    const [isLoadingAvatar, setIsLoadingAvatar] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const fetchAvatar = async () => {
            if (profile.hasHeadshot) {
                setIsLoadingAvatar(true);
                const url = await api.getHeadshotUrl(profile.id);
                if (isMounted) {
                    setAvatarSrc(url);
                    setIsLoadingAvatar(false);
                }
            } else {
                setAvatarSrc(null);
                setIsLoadingAvatar(false);
            }
        };
        fetchAvatar();
        return () => { isMounted = false; };
    }, [profile.hasHeadshot, profile.id]);

    return ( 
        <header className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                    {onBack && (<button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"><ArrowLeft size={24} className="text-[#323A40]" /></button>)}
                    <div className="relative">
                        <button 
                            onClick={canEditAvatar ? onAvatarClick : undefined} 
                            disabled={!canEditAvatar}
                            className={`relative w-[64px] h-[64px] rounded-full group ${canEditAvatar ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                            {isLoadingAvatar ? (
                                <div className="bg-gray-200 rounded-full w-full h-full animate-pulse"></div>
                            ) : avatarSrc ? (
                                <img src={avatarSrc} alt="User headshot" className="w-full h-full rounded-full object-cover" />
                            ) : (
                                <div className="bg-[#323A40] text-white p-3 rounded-full w-full h-full flex items-center justify-center">
                                    <User size={32} />
                                </div>
                            )}
                            {canEditAvatar && (
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-full flex items-center justify-center transition-opacity">
                                    <Upload size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            )}
                        </button>
                        {canEditAvatar && avatarSrc && (
                            <button 
                                onClick={onDeleteAvatar} 
                                className="absolute -top-1 -right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                title="Delete Headshot"
                            >
                                <Trash2 size={12} />
                            </button>
                        )}
                    </div>
                    <div>
                        <h1 className="text-2xl font-normal normal-case">{profile.name}</h1>
                        <div className="text-xs text-gray-500 font-mono">
                            <span>EMP#: {profile.employeeNumber}</span>
                        </div>
                        <p className="text-gray-500 font-normal normal-case mt-1">{profile.title} - Viewing Year: {selectedTitleYear?.year || profile.year}</p>
                    </div>
                </div>
                {currentUser.permissionId === 3 && (
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        <button onClick={() => setViewAs('employee')} className={`px-3 py-1 text-sm uppercase font-semibold rounded-md transition-colors ${viewAs === 'employee' ? 'bg-white text-[#323A40] shadow' : 'text-gray-600 hover:text-[#323A40]'}`}>Employee</button>
                        <button onClick={() => setViewAs('manager')} className={`px-3 py-1 text-sm uppercase font-semibold rounded-md transition-colors ${viewAs === 'manager' ? 'bg-white text-[#323A40] shadow' : 'text-gray-600 hover:text-[#323A40]'}`}>Manager</button>
                    </div>
                )}
            </div>
        </header> 
    );
}

function PerformanceChartCard({ skills, allTitleYears, employeeProfile, selectedTitle, selectedTitleYearId, onTitleYearSelect, onTitleSelect }) {
    const chartData = useMemo(() => {
        const skillsByTitleYear = {};
        if (skills) {
            skills.forEach(skill => {
                if (!skillsByTitleYear[skill.titleYearId]) {
                    skillsByTitleYear[skill.titleYearId] = [];
                }
                skillsByTitleYear[skill.titleYearId].push(skill);
            });
        }

        const relevantYearIds = new Set();
        if (skills) {
            skills.forEach(skill => relevantYearIds.add(skill.titleYearId));
        }
        if (employeeProfile?.currentTitleYearId) {
            relevantYearIds.add(employeeProfile.currentTitleYearId);
        }
        const relevantYears = allTitleYears.filter(ty => relevantYearIds.has(ty.id));

        return relevantYears.map(titleYear => {
            const skillsForThisYear = skillsByTitleYear[titleYear.id] || [];
            let score = 0;
            if (skillsForThisYear.length > 0) {
                const total = skillsForThisYear.reduce((sum, s) => sum + s.managerRating, 0);
                score = total / skillsForThisYear.length;
            }
            return { titleYearId: titleYear.id, title: titleYear.title, year: titleYear.year, score: parseFloat(score.toFixed(2)) };
        }).sort((a,b) => a.year - b.year);

    }, [skills, allTitleYears, employeeProfile]);

    const handleBarClick = (data) => {
        if (data && data.activePayload && data.activePayload[0]) {
            const payload = data.activePayload[0].payload;
            if (payload && payload.titleYearId) {
                 onTitleYearSelect(payload.titleYearId);
            }
        }
    };
    
    const uniqueTitles = useMemo(() => {
        if (!chartData) return [];
        const titles = new Set(chartData.map(d => d.title));
        return Array.from(titles);
    }, [chartData]);
    
    const filteredChartData = useMemo(() => {
        if (!selectedTitle) return chartData;
        return chartData.filter(d => d.title === selectedTitle);
    }, [chartData, selectedTitle]);
    
    const handleTitleSelect = (title) => {
        onTitleSelect(title);
    };

    return ( 
        <Card title="Yearly Performance Overview" icon={<BarChart className="text-[#323A40]" />}>
            {uniqueTitles.length > 1 && (
                <div className="flex justify-center gap-2 mb-4 border-b pb-4">
                    {uniqueTitles.map(title => (
                        <button 
                            key={title}
                            onClick={() => handleTitleSelect(title)}
                            className={`py-1 px-3 rounded-lg text-sm font-semibold uppercase transition-colors ${
                                title === selectedTitle 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {title}
                        </button>
                    ))}
                </div>
            )}

            <div className="flex justify-center gap-2 mb-4">
                {filteredChartData.map(entry => (
                    <button 
                        key={entry.titleYearId}
                        onClick={() => onTitleYearSelect(entry.titleYearId)}
                        className={`py-1 px-3 rounded-lg text-sm font-semibold uppercase transition-colors ${
                            entry.titleYearId === selectedTitleYearId 
                            ? 'bg-[#323A40] text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {entry.year}
                    </button>
                ))}
            </div>

            <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                    <BarChart data={filteredChartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }} onClick={handleBarClick}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="year" tick={{ fontSize: 12, fontFamily: 'Saira' }} interval={0}/>
                        <YAxis allowDecimals={false} domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 12, fontFamily: 'Saira' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontFamily: 'Saira' }} />
                        <Bar dataKey="score" name="Avg. Manager Rating" radius={[4, 4, 0, 0]} barSize={50} style={{cursor: 'pointer'}}>
                            {filteredChartData.map((entry) => ( 
                                <Cell key={`cell-${entry.titleYearId}`} fill={entry.titleYearId === selectedTitleYearId ? '#323A40' : '#789904'} /> 
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card> 
    );
}

function OneOnOneMeetingsCard({ meetings, viewAs, isLocked, onMeetingsSave }) {
    const [localMeetings, setLocalMeetings] = useState(meetings);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setLocalMeetings(meetings);
        setIsDirty(false);
    }, [meetings]);

    const handleInputChange = (quarter, value) => {
        const numericValue = parseInt(value, 10);
        if (!isNaN(numericValue) && numericValue >= 0) {
            setLocalMeetings(prev => ({ ...prev, [quarter]: numericValue }));
            setIsDirty(true);
        }
    };

    const handleSave = () => {
        onMeetingsSave(localMeetings);
        setIsDirty(false);
    };

    return (
        <Card title="1:1 Meetings" icon={<User className="text-[#323A40]" />}>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    {(['Q1', 'Q2', 'Q3', 'Q4']).map(q => (
                        <div key={q} className="flex items-center gap-2">
                            <label className="font-semibold text-sm text-gray-600 uppercase">{q}:</label>
                            <input 
                                type="number" 
                                min="0"
                                value={localMeetings[q] || 0} 
                                onChange={(e) => handleInputChange(q, e.target.value)} 
                                readOnly={viewAs !== 'manager' || isLocked} 
                                className="w-full p-1.5 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-[#789904] focus:outline-none read-only:bg-gray-100 read-only:text-gray-500 read-only:cursor-not-allowed font-normal" 
                            />
                        </div>
                    ))}
                </div>
                {isDirty && !isLocked && (
                    <div className="flex justify-end pt-2">
                        <button onClick={handleSave} className="flex items-center gap-2 bg-[#323A40] text-white text-sm font-semibold uppercase py-1.5 px-4 rounded-lg hover:bg-[#789904] transition-colors">
                            <Save size={16} /> Save Meetings
                        </button>
                    </div>
                )}
            </div>
        </Card>
    );
}

function SkillsAssessmentCard({ skills, viewAs, onRateClick, isLocked, currentUser }) {
    const canEditEmployee = viewAs === 'employee' && !isLocked;
    const canEditManager = viewAs === 'manager' && !isLocked;
    const [showInactive, setShowInactive] = useState(false);

    const skillsToDisplay = useMemo(() => {
        const grouped = {};
        const filteredSkills = skills.filter(s => showInactive || s.isActive);
        filteredSkills.forEach(skill => {
            const category = skill.categoryName;
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push(skill);
        });
        return grouped;
    }, [skills, showInactive]);

    return ( 
        <Card title="Skill Details" icon={<BrainCircuit className="text-[#323A40]" />} actions={ currentUser.permissionId === 3 && ( <label className="flex items-center cursor-pointer text-sm"><span className="mr-2 font-semibold uppercase text-gray-500">Show Inactive</span><div className="relative"><input type="checkbox" checked={showInactive} onChange={() => setShowInactive(!showInactive)} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div></div></label> ) }>
            <div className="space-y-4"> 
                {Object.keys(skillsToDisplay).sort().map(category => (
                    <div key={category}>
                        <h4 className="font-semibold text-md text-gray-600 italic mb-2">{category}</h4>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4 pl-4">
                            {skillsToDisplay[category].map(masterSkill => { 
                                const rating = masterSkill || {employeeRating: 0, managerRating: 0};
                                return (
                                    <React.Fragment key={masterSkill.skillId}>
                                        <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                                            <span className="text-sm font-normal normal-case">{masterSkill.skillName}</span>
                                            <button onClick={() => canEditEmployee && onRateClick(masterSkill.skillId, 'employeeRating')} className={`w-10 h-10 flex items-center justify-center text-lg font-bold rounded-md ${canEditEmployee ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>{rating.employeeRating}</button>
                                        </div>
                                        <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                                            <button onClick={() => canEditManager && onRateClick(masterSkill.skillId, 'managerRating')} className={`w-10 h-10 flex items-center justify-center text-lg font-bold rounded-md ${canEditManager ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>{rating.managerRating}</button>
                                        </div>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </div>
                ))} 
                {Object.keys(skillsToDisplay).length === 0 && <p className="text-center text-gray-500 text-sm py-4 font-normal normal-case">No skills defined for this year.</p>}
            </div>
        </Card> 
    );
}
function RatingModal({ skillRating, skillMaster, ratingType, onClose, onSave }) { const [hoveredRating, setHoveredRating] = useState(null); const [selectedRating, setSelectedRating] = useState(skillRating ? (ratingType === 'managerRating' ? skillRating.managerRating : skillRating.employeeRating) : 0); const handleSave = () => { onSave(skillMaster.id, ratingType, selectedRating); }; return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]"><div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg text-[#323A40]"><h3 className="text-xl font-normal normal-case mb-2">Rate Skill: {skillMaster.name}</h3><div className="flex justify-around my-8">{[0, 1, 2, 3, 4, 5].map(value => (<button key={value} onClick={() => setSelectedRating(value)} onMouseEnter={() => setHoveredRating(value)} onMouseLeave={() => setHoveredRating(null)} className={`w-16 h-16 flex items-center justify-center text-2xl font-bold rounded-full border-4 transition-all ${selectedRating === value ? 'bg-[#323A40] text-white border-gray-300' : 'bg-gray-100 text-[#323A40] border-gray-200 hover:border-[#789904]'}`}>{value}</button>))}</div> <div className="bg-gray-50 p-4 rounded-lg min-h-[6rem] text-center flex items-center justify-center"><p className="text-gray-600 font-normal normal-case">{skillMaster[`descriptionLevel${hoveredRating !== null ? hoveredRating : selectedRating}`] || "Select a rating from 0 (No Exposure) to 5 (Expert)."}</p></div> <div className="flex justify-end gap-3 mt-8"><button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button><button onClick={handleSave} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904]">Save</button></div></div></div> ); }
function AddCertTypeModal({ onClose, onSave }) { const [name, setName] = useState(''); const handleSave = () => { if (name.trim()) onSave(name.trim()); }; return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]"><div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm text-[#323A40]"><h3 className="text-xl font-normal normal-case mb-4">Add Certification Type</h3><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Scissor Lift Certified" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /><div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button><button onClick={handleSave} disabled={!name.trim()} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button></div></div></div> ); }
function EditCertTypeModal({ cert, onClose, onSave }) { const [name, setName] = useState(cert.name); const [isActive, setIsActive] = useState(cert.isActive); const handleSave = () => { if (name.trim()) onSave(cert.id, name.trim(), isActive); }; return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]"><div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm text-[#323A40]"><h3 className="text-xl font-normal normal-case mb-4">Edit Certification Type</h3><div className="space-y-4"><input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /><label className="flex items-center cursor-pointer"><span className="mr-3 text-sm font-semibold uppercase text-gray-600">Active</span><div className="relative"><input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div></div></label></div><div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button><button onClick={handleSave} disabled={!name.trim()} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button></div></div></div> ); }
function EditSkillModal({ skill, onClose, onSave, categories, titleYears }) {
    const [editedSkill, setEditedSkill] = useState(skill);

    const handleFieldChange = (field, value) => {
        setEditedSkill(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl text-[#323A40]">
                <h3 className="text-xl font-normal normal-case mb-4">Edit Skill</h3>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Skill Name</label><input type="text" value={editedSkill.name} onChange={(e) => handleFieldChange('name', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /></div>
                        <div><label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Category</label><select value={editedSkill.categoryName} onChange={(e) => handleFieldChange('categoryName', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal">{categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}</select></div>
                        <div><label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Title/Year</label><select value={editedSkill.titleYearId} onChange={(e) => handleFieldChange('titleYearId', parseInt(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal">{titleYears.map(ty => <option key={ty.id} value={ty.id}>{`${ty.title} - Year ${ty.year}`}</option>)}</select></div>
                    </div>
                    {[1, 2, 3, 4, 5].map(level => (
                        <div key={level}>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Level {level} - {['Awareness', 'Basic', 'Competent', 'Proficient', 'Expert'][level - 1]}</label>
                            <textarea value={editedSkill[`descriptionLevel${level}`] || ''} onChange={(e) => handleFieldChange(`descriptionLevel${level}`, e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" />
                        </div>
                    ))}
                    <label className="flex items-center cursor-pointer pt-4">
                        <span className="mr-3 text-sm font-semibold uppercase text-gray-600">Active</span>
                        <div className="relative">
                            <input type="checkbox" checked={editedSkill.isActive} onChange={(e) => handleFieldChange('isActive', e.target.checked)} className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div>
                        </div>
                    </label>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onSave(skill.id, editedSkill)} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904]">Save</button>
                </div>
            </div>
        </div>
    );
}
function AddSkillModal({ onClose, onSave, categories, titleYears }) {
    const [newSkill, setNewSkill] = useState({ 
        name: '', 
        titleYearId: titleYears[0]?.id || '', 
        categoryName: categories[0]?.name || '', 
        descriptionLevel1: '', 
        descriptionLevel2: '', 
        descriptionLevel3: '', 
        descriptionLevel4: '', 
        descriptionLevel5: '', 
        isActive: true 
    });

    const handleFieldChange = (field, value) => {
        setNewSkill(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl text-[#323A40]">
                <h3 className="text-xl font-normal normal-case mb-4">Add New Skill</h3>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Skill Name</label>
                            <input type="text" value={newSkill.name} onChange={(e) => handleFieldChange('name', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Category</label>
                            <select value={newSkill.categoryName} onChange={(e) => handleFieldChange('categoryName', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal">{categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}</select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Title/Year</label>
                            <select value={newSkill.titleYearId} onChange={(e) => handleFieldChange('titleYearId', parseInt(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal">{titleYears.map(ty => <option key={ty.id} value={ty.id}>{`${ty.title} - Year ${ty.year}`}</option>)}</select>
                        </div>
                    </div>
                    {[1, 2, 3, 4, 5].map(level => (
                        <div key={level}>
                            <label className="block text-sm font-semibold uppercase text-[#789904] mb-1">Level {level} - {['Awareness', 'Basic', 'Competent', 'Proficient', 'Expert'][level - 1]}</label>
                            <textarea value={newSkill[`descriptionLevel${level}`]} onChange={(e) => handleFieldChange(`descriptionLevel${level}`, e.target.value)} rows={2} placeholder={`Description for level ${level}...`} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" />
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onSave(newSkill)} disabled={!newSkill.name.trim() || !newSkill.categoryName} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button>
                </div>
            </div>
        </div>
    );
}
function AddCategoryModal({ onClose, onSave }) { const [name, setName] = useState(''); const handleSave = () => { if (name.trim()) onSave(name.trim()); }; return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]"><div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm text-[#323A40]"><h3 className="text-xl font-normal normal-case mb-4">Add New Category</h3><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Project Management" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /><div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button><button onClick={handleSave} disabled={!name.trim()} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button></div></div></div> ); }
function EditCategoryModal({ category, onClose, onSave }) { const [name, setName] = useState(category.name); const [isActive, setIsActive] = useState(category.isActive); const handleSave = () => { if (name.trim()) onSave(category.id, name.trim(), isActive); }; return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]"><div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm text-[#323A40]"><h3 className="text-xl font-normal normal-case mb-4">Edit Category</h3><div className="space-y-4"><input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#789904] focus:outline-none font-normal" /><label className="flex items-center cursor-pointer"><span className="mr-3 text-sm font-semibold uppercase text-gray-600">Active</span><div className="relative"><input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-[#789904] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#323A40]"></div></div></label></div><div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button><button onClick={handleSave} disabled={!name.trim()} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button></div></div></div> ); }

function ImageUploadModal({ onClose, onSave }) {
    const [upImg, setUpImg] = useState();
    const imgRef = React.useRef(null);
    const previewCanvasRef = React.useRef(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();

    const onSelectFile = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener('load', () => setUpImg(reader.result?.toString() || ''));
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const crop = centerCrop(
            makeAspectCrop({ unit: '%', width: 90 }, 1, width, height),
            width, height
        );
        setCrop(crop);
    };

    useEffect(() => {
        if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
            return;
        }
        const image = imgRef.current;
        const canvas = previewCanvasRef.current;
        const ctx = canvas.getContext('2d');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);
        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';
        const cropX = completedCrop.x * scaleX;
        const cropY = completedCrop.y * scaleY;
        ctx.drawImage(
            image,
            cropX, cropY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0, 0,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        );
    }, [completedCrop]);

    const handleSaveCrop = () => {
        const canvas = previewCanvasRef.current;
        if (!canvas) throw new Error('Crop canvas does not exist');
        
        canvas.toBlob((blob) => {
            if (!blob) throw new Error('Failed to create blob');
            onSave(blob);
        }, 'image/jpeg', 0.95);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-['Saira',_sans_serif]">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg text-[#323A40]">
                <h3 className="text-xl font-normal normal-case mb-4">Upload and Crop Headshot</h3>
                <div className="space-y-4 text-center">
                    <input type="file" accept="image/*" onChange={onSelectFile} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#323A40] file:text-white hover:file:bg-[#789904]" />
                    
                    {upImg && (
                        <ReactCrop
                            crop={crop}
                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                            circularCrop={true}
                        >
                            <img
                                ref={imgRef}
                                alt="Crop me"
                                src={upImg}
                                onLoad={onImageLoad}
                                style={{ maxHeight: '60vh' }}
                            />
                        </ReactCrop>
                    )}
                    <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 text-[#323A40] font-semibold uppercase rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSaveCrop} disabled={!completedCrop} className="py-2 px-6 bg-[#323A40] text-white font-semibold uppercase rounded-lg hover:bg-[#789904] disabled:bg-gray-400">Save</button>
                </div>
            </div>
        </div>
    );
}